"use client";

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import i18next from '@/lib/i18n';

const t = (k: string): string => String(i18next.t(k));
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Camera, ChevronRight, Wand2 } from 'lucide-react';
import { getImagePluginMeta, type ImagePluginId, normalizeImagePlugins } from '@flowmuse/models/image/plugins/registry';
import CameraControl, {
  buildCameraControlApplyPayloadFromSelection,
  formatCameraControlSummary,
  type CameraControlApplyPayload,
  type CameraControlSelection,
} from '@/components/nodes/tool/camera/CameraControl';

type ImagePluginButtonProps = {
  plugins: ImagePluginId[] | unknown;
  enabledPlugins?: string[] | unknown;
  onEnabledPluginsChange?: (next: ImagePluginId[]) => void;
  cameraValue?: Partial<CameraControlSelection>;
  onCameraApply?: (payload: CameraControlApplyPayload) => void;
  onCameraClose?: () => void;
  disabled?: boolean;
};

const MENU_CONTENT_CLASS =
  "fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-2xl min-w-[220px] p-1.5 z-[200]";
const MENU_ITEM_CLASS =
  "rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-[11px] font-medium flex items-center gap-2";

export function ImagePluginButton({
  plugins,
  enabledPlugins,
  onEnabledPluginsChange,
  cameraValue,
  onCameraApply,
  onCameraClose,
  disabled,
}: ImagePluginButtonProps) {
  const normalized = useMemo(() => normalizeImagePlugins(plugins), [plugins]);
  const enabled = useMemo(() => new Set(normalizeImagePlugins(enabledPlugins)), [enabledPlugins]);
  const anyEnabled = enabled.size > 0;
  const cameraSummary = useMemo(() => formatCameraControlSummary(cameraValue), [cameraValue]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<ImagePluginId | null>(null);
  const [pendingEnable, setPendingEnable] = useState<ImagePluginId | null>(null);

  if (!normalized.length) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "rounded-md h-8 px-2 text-xs",
              !disabled && anyEnabled && 'bg-primary/10 text-primary hover:bg-primary/20'
            )}
            disabled={disabled}
            title={t("Plugins")}
          >
            <Wand2 size={12} className="mr-1" />
            {t('Plugins')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          sideOffset={8}
          className={MENU_CONTENT_CLASS}
        >
          {normalized.map((id) => {
            const meta = getImagePluginMeta(id);
            if (!meta) return null;
            const isEnabled = enabled.has(meta.id);
            const isPending = pendingEnable === meta.id;
            const isCamera = meta.id === 'camera';
            return (
              <DropdownMenuItem
                key={meta.id}
                className={MENU_ITEM_CLASS}
                onSelect={(e) => {
                  e.preventDefault();
                  // keep menu open; open only when clicking label (handled below)
                }}
              >
                <button
                  type="button"
                  className={
                    disabled || (!isEnabled && !isPending && !isCamera)
                      ? 'flex-1 text-left text-muted-foreground/60'
                      : 'flex-1 text-left hover:underline underline-offset-4'
                  }
                  disabled={disabled || (!isCamera && !isEnabled && !isPending)}
                  title={isCamera || isEnabled || isPending ? t('Click to open settings') : t('Enable the switch on the right first')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabled) return;
                    // camera 插件：允许在未启用时也打开设置，点击应用后才算启用
                    if (!isCamera && !isEnabled && !isPending) return;
                    if (isCamera && !isEnabled && cameraSummary.configured) {
                      // Already configured: enable directly without opening the panel.
                      const selection = cameraValue as any as CameraControlSelection;
                      try {
                        onCameraApply?.(buildCameraControlApplyPayloadFromSelection(selection));
                      } catch {}
                      const next = new Set(enabled);
                      next.add('camera');
                      onEnabledPluginsChange?.(Array.from(next));
                      setPendingEnable(null);
                      return;
                    }
                    setActive(meta.id);
                    if (isCamera && !isEnabled) {
                      setPendingEnable('camera');
                    }
                    setOpen(true);
                  }}
                >
                  <span className="flex items-center justify-between gap-2 w-full">
                    <span className="flex flex-col min-w-0">
                      <span className="inline-flex items-center gap-2 min-w-0">
                        {meta.id === 'camera' ? <Camera size={14} className="text-primary/80" /> : null}
                        <span className="truncate">{meta.label}</span>
                      </span>
                      {isCamera ? (
                        <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground truncate" title={cameraSummary.text}>
                          {cameraSummary.text}
                        </span>
                      ) : null}
                    </span>
                    {isEnabled || isPending ? <ChevronRight size={14} className="text-muted-foreground" /> : null}
                  </span>
                </button>

                <div
                  className="shrink-0"
                  onClick={(e) => {
                    // prevent dropdown item selection closing
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Switch
                    checked={isEnabled || isPending}
                    disabled={disabled}
                    onCheckedChange={(nextEnabled) => {
                      if (disabled) return;

                      // Camera plugin: enabling requires configuration first.
                      if (isCamera) {
                        if (nextEnabled) {
                          if (cameraSummary.configured) {
                            // Already configured: enable directly without opening the panel.
                            const selection = cameraValue as any as CameraControlSelection;
                            try {
                              onCameraApply?.(buildCameraControlApplyPayloadFromSelection(selection));
                            } catch {}
                            const next = new Set(enabled);
                            next.add('camera');
                            onEnabledPluginsChange?.(Array.from(next));
                            setPendingEnable(null);
                            return;
                          }
                          setActive('camera');
                          setPendingEnable('camera');
                          setOpen(true);
                          return;
                        }
                        // Disabling camera: persist off + clear injected prompt
                        const next = new Set(enabled);
                        next.delete('camera');
                        onEnabledPluginsChange?.(Array.from(next));
                        setPendingEnable((cur) => (cur === 'camera' ? null : cur));
                        onCameraClose?.();
                        if (active === 'camera') {
                          setOpen(false);
                          setActive(null);
                        }
                        return;
                      }

                      const next = new Set(enabled);
                      if (nextEnabled) next.add(meta.id);
                      else next.delete(meta.id);
                      const arr = Array.from(next);
                      onEnabledPluginsChange?.(arr);

                      // If turning off currently open plugin, close it.
                      if (!nextEnabled && active === meta.id) {
                        setOpen(false);
                        setActive(null);
                      }
                    }}
                  />
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            if (active === 'camera') {
              // Cancel camera config: clear pending + disable + clear injected prompt
              setPendingEnable(null);
              try {
                const nextEnabled = new Set(enabled);
                nextEnabled.delete('camera');
                onEnabledPluginsChange?.(Array.from(nextEnabled));
              } catch {}
              onCameraClose?.();
            }
            setActive(null);
          }
        }}
      >
        <DialogContent className="p-0 bg-transparent border-0 shadow-none [&>button]:hidden">
          {active === 'camera' ? (
            <CameraControl
              value={cameraValue}
              onApply={(payload) => {
                onCameraApply?.(payload);
                // Apply means "enabled" for camera plugin.
                const next = new Set(enabled);
                next.add('camera');
                onEnabledPluginsChange?.(Array.from(next));
                setPendingEnable(null);
                // Close dialog after applying
                setOpen(false);
                setActive(null);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}


